import {
  Box,
  Typography,
  Button,
  Stack,
  InputAdornment,
  FormControlLabel,
} from "@mui/material";
import { useState } from "react";
import { CustomInput } from "global/components";
import { AppModal } from "global/components/AppModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomSwitch from "global/components/CustomSwitch/CustomSwitch";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import AddIcon from "@mui/icons-material/Add";
import LanguageIcon from "@mui/icons-material/Language";
import DeleteIcon from "assets/icons1/Delete_Icon.svg";
import {
  addIpAllowlistEntries,
  updateIpAllowlistEntries,
} from "./WhitelistIPsService";
import {
  IpAllowlistEntry,
  IpAllowlistEntryInput,
  IpAllowlistEntryPatch,
} from "./WhitelistIPsTypes";
import { MAX_LABEL_LENGTH, validateIpOrCidr } from "./WhitelistIPsConstant";
import {
  focusFirstInvalidField,
  InvalidFieldTarget,
} from "utils/accessibility";

const IP_VALUE_FIELD_ID = "whitelist_ip_value_";
const IP_LABEL_FIELD_ID = "whitelist_ip_label_";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  onSuccess?: Function;
  editEntry?: IpAllowlistEntry | null;
  // Pre-populate the first row (used by the guarded-activation "add my IP" flow).
  prefillValue?: string;
  prefillLabel?: string;
}

interface IPRow {
  id: number;
  value: string;
  label: string;
  enabled: boolean;
  valueError: string;
  labelError: string;
}

// Brand accent (inkfree primary)
const ACCENT = "primary.main";

const newRow = (): IPRow => ({
  id: Date.now() + Math.floor(performance.now()),
  value: "",
  label: "",
  enabled: true,
  valueError: "",
  labelError: "",
});

const AddWhitelistIPModal = (props: CustomProps) => {
  const isEditMode = !!props.editEntry;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ipRows, setIpRows] = useState<IPRow[]>(
    isEditMode
      ? [
          {
            id: Date.now(),
            value: props.editEntry!.value,
            label: props.editEntry!.label,
            enabled: props.editEntry!.enabled,
            valueError: "",
            labelError: "",
          },
        ]
      : [
          {
            ...newRow(),
            value: props.prefillValue ?? "",
            label: props.prefillLabel ?? "",
          },
        ],
  );

  const handleValidation = () => {
    let isValid = true;

    const updatedRows = ipRows.map((row) => {
      const valueError = validateIpOrCidr(row.value);
      const label = row.label.trim();
      let labelError = "";

      if (!label) labelError = "Please enter a label";
      else if (label.length > MAX_LABEL_LENGTH)
        labelError = `Label must be ${MAX_LABEL_LENGTH} characters or fewer`;

      if (valueError || labelError) isValid = false;

      return { ...row, valueError, labelError };
    });

    setIpRows(updatedRows);
    if (!isValid) {
      const targets: InvalidFieldTarget[] = [];
      updatedRows.forEach((row, index) => {
        targets.push(row.valueError && `${IP_VALUE_FIELD_ID}${index}`);
        targets.push(row.labelError && `${IP_LABEL_FIELD_ID}${index}`);
      });
      focusFirstInvalidField(targets);
    }
    return isValid;
  };

  const handleValueChange = (index: number, value: string) => {
    setIpRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, value, valueError: "" } : row,
      ),
    );
  };

  const handleLabelChange = (index: number, label: string) => {
    setIpRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, label, labelError: "" } : row,
      ),
    );
  };

  const handleEnabledChange = (index: number, enabled: boolean) => {
    setIpRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, enabled } : row)),
    );
  };

  const handleAddRow = () => {
    setIpRows((prev) => [...prev, newRow()]);
  };

  const handleRemoveRow = (index: number) => {
    if (ipRows.length === 1) return;
    setIpRows((prev) => prev.filter((_, i) => i !== index));
  };

  const submitHandler = async () => {
    try {
      if (!handleValidation()) return;
      setIsLoading(true);

      if (isEditMode) {
        // Partial merge — only send fields that actually changed.
        const original = props.editEntry!;
        const row = ipRows[0];
        const patch: IpAllowlistEntryPatch = { id: original.id };
        const trimmedValue = row.value.trim();
        const trimmedLabel = row.label.trim();
        if (trimmedValue !== original.value) patch.value = trimmedValue;
        if (trimmedLabel !== original.label) patch.label = trimmedLabel;
        if (row.enabled !== original.enabled) patch.enabled = row.enabled;

        if (Object.keys(patch).length === 1) {
          // Nothing changed — close without a no-op request.
          props.handleDialogClose();
          return;
        }

        await updateIpAllowlistEntries([patch]);
        openSuccessNotification("Whitelist IP updated successfully");
      } else {
        const entries: IpAllowlistEntryInput[] = ipRows.map((row) => ({
          value: row.value.trim(),
          label: row.label.trim(),
          enabled: row.enabled,
        }));
        await addIpAllowlistEntries(entries);
        openSuccessNotification(
          entries.length > 1
            ? "Whitelist IPs added successfully"
            : "Whitelist IP added successfully",
        );
      }

      props.onSuccess?.();
      props.handleDialogClose();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getEntriesContent = () => (
    <Stack spacing={!isEditMode ? 2.5 : 1.5} sx={{ textAlign: "left" }}>
      {!isEditMode && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: "text.primary" }}
          >
            IP entries
          </Typography>
          <Box sx={{ fontSize: "12px", color: ACCENT, px: 1.5, py: 0.25 }}>
            {ipRows.length} {ipRows.length === 1 ? "entry" : "entries"}
          </Box>
        </Stack>
      )}

      {/* Entry rows */}
      <Box
        sx={{
          maxHeight: "320px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pr: 0.5,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: (theme) => theme.palette.neutral[300],
            borderRadius: "4px",
          },
        }}
      >
        {ipRows.map((row, index) => (
          <Box
            key={row.id}
            sx={{
              border: "1px solid",
              borderColor: "neutral.200",
              borderRadius: "10px",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box flex={1}>
                <CustomInput
                  required
                  id={`${IP_VALUE_FIELD_ID}${index}`}
                  placeHolder="e.g. 192.168.1.1, 10.0.0.0/24 or 2001:db8::/32"
                  name="ipValue"
                  value={row.value}
                  onChange={(e: any) =>
                    handleValueChange(index, e.target.value)
                  }
                  error={!!row.valueError}
                  helperText={row.valueError}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
                      outline: "2px solid",
                      outlineColor: "formField.focusBorder",
                      outlineOffset: "2px",
                    },
                  }}
                  propsToInputElement={{
                    maxLength: 100,
                    "aria-label": "IP address or range",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {!isEditMode && (
                <Box
                  component="button"
                  type="button"
                  aria-label={`Remove IP entry ${index + 1}`}
                  disabled={ipRows.length === 1}
                  onClick={() => handleRemoveRow(index)}
                  sx={{
                    appearance: "none",
                    background: "transparent",
                    p: 0,
                    font: "inherit",
                    width: 38,
                    height: 38,
                    border: "1px solid",
                    borderColor: "neutral.300",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: ipRows.length === 1 ? "not-allowed" : "pointer",
                    opacity: ipRows.length === 1 ? 0.4 : 1,
                    flexShrink: 0,
                    mt: "2px",
                    transition: "all 0.15s ease",
                    "& img": {
                      width: 18,
                      height: 18,
                      transition: "all 0.15s ease",
                    },
                    "&:hover": {
                      borderColor:
                        ipRows.length > 1 ? "error.main" : "neutral.300",
                      backgroundColor:
                        ipRows.length > 1 ? "error.lightest" : "transparent",
                    },
                    "&:hover img": {
                      filter:
                        ipRows.length > 1
                          ? "brightness(0) saturate(100%) invert(32%) sepia(94%) saturate(2487%) hue-rotate(338deg) brightness(97%) contrast(96%)"
                          : "none",
                    },
                  }}
                >
                  <Box component="img" src={DeleteIcon} alt="" aria-hidden="true" />
                </Box>
              )}
            </Stack>

            <CustomInput
              required
              id={`${IP_LABEL_FIELD_ID}${index}`}
              label="Label"
              placeHolder="e.g. NYC office"
              name="label"
              value={row.label}
              onChange={(e: any) => handleLabelChange(index, e.target.value)}
              error={!!row.labelError}
              helperText={row.labelError}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
                  outline: "2px solid",
                  outlineColor: "formField.focusBorder",
                  outlineOffset: "2px",
                },
              }}
              propsToInputElement={{ maxLength: MAX_LABEL_LENGTH }}
            />

            <FormControlLabel
              control={
                <CustomSwitch
                  checked={row.enabled}
                  onChange={(e: any) =>
                    handleEnabledChange(index, e.target.checked)
                  }
                  name="enabled"
                  aria-label={`Enable IP entry ${index + 1}`}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: "13px", color: "text.secondary", ml: 1 }}
                >
                  {row.enabled ? "Enabled" : "Disabled"}
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          </Box>
        ))}
      </Box>

      {!isEditMode && (
        <Button
          variant="outlined"
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          sx={{
            borderColor: "neutral.300",
            color: "text.secondary",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              borderColor: ACCENT,
              color: ACCENT,
              backgroundColor: "transparent",
            },
          }}
        >
          Add Another IP
        </Button>
      )}
    </Stack>
  );

  const getPrimaryLabel = () => {
    if (isLoading) return isEditMode ? "Saving..." : "Adding...";
    return isEditMode ? "Save Changes" : "Add Whitelist IP";
  };

  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <AppModal
        open={props.isVisible}
        onClose={() => props.handleDialogClose()}
        title={isEditMode ? "Edit Whitelist IP" : "Add Whitelist IPs"}
        subtitle={
          isEditMode
            ? "Update the whitelisted IP address, label or status"
            : "Add IPs or CIDR ranges that may reach this account"
        }
        size="md"
        busy={isLoading}
        primaryLabel={getPrimaryLabel()}
        onPrimary={submitHandler}
        primaryLoading={isLoading}
        cancelLabel="Cancel"
        onCancel={() => props.handleDialogClose()}
      >
        <Stack>{getEntriesContent()}</Stack>
      </AppModal>
    </>
  );
};

export default AddWhitelistIPModal;
